import React, { useState, useMemo, useEffect } from 'react';
import './InPersonMeetingParticipantsModal.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalHeader, ModalBody, Button } from 'reactstrap';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { useStateSelector } from '../../../../store/selectors';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { EngagementInPersonMeetingParticipant } from '../../../../slices/marketplace/meetings/models/engagement-in-person-meeting-participant';
import AddParticipantFormPopup from './AddParticipantFormPopup';

interface InPersonMeetingParticipantsModalProps {
    isVisible: boolean;
    onClose: () => void;
    engagementId: number;
    meetingId: string;
    inPersonMeetingParticipants: EngagementInPersonMeetingParticipant[];
    isReadOnly?: boolean;
}

const InPersonMeetingParticipantsModal = (
    props: InPersonMeetingParticipantsModalProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const party = useCurrentMarketplaceParty(props.engagementId);

    const [isAddParticipantPopupVisible, setIsAddParticipantPopupVisible] =
        useState(false);
    const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
        useState(false);
    const [participantToDelete, setParticipantToDelete] =
        useState<EngagementInPersonMeetingParticipant | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    const [localParticipants, setLocalParticipants] = useState<EngagementInPersonMeetingParticipant[]>(
        props.inPersonMeetingParticipants || []
    );

    useEffect(() => {
        setLocalParticipants(props.inPersonMeetingParticipants || []);
    }, [props.inPersonMeetingParticipants]);

    const sortedParticipants = useMemo(() => {
        if (
            !localParticipants ||
            localParticipants.length === 0
        ) {
            return [];
        }

        const participants = [...localParticipants];

        if (party === TimelineParty.Buyer) {
            return participants.sort((a, b) => {
                if (
                    a.party === TimelineParty.Buyer &&
                    b.party !== TimelineParty.Buyer
                )
                    return -1;
                if (
                    a.party !== TimelineParty.Buyer &&
                    b.party === TimelineParty.Buyer
                )
                    return 1;
                return 0;
            });
        } else {
            return participants.sort((a, b) => {
                if (
                    a.party === TimelineParty.Seller &&
                    b.party !== TimelineParty.Seller
                )
                    return -1;
                if (
                    a.party !== TimelineParty.Seller &&
                    b.party === TimelineParty.Seller
                )
                    return 1;
                return 0;
            });
        }
    }, [localParticipants, party]);

    const canDeleteParticipant = (
        participant: EngagementInPersonMeetingParticipant
    ): boolean => {
        if (party !== TimelineParty.Buyer && party !== TimelineParty.Seller) {
            return false;
        }
        return participant.party === party;
    };

    const refreshParticipants = async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const response = await axios.get(
                `/api/Marketplace/Engagements/${props.engagementId}`
            );
            
            if (response.status === 200 && response.data?.engagementData?.meetingProposals) {
                const proposals = response.data.engagementData.meetingProposals;
                const proposal = proposals.find(
                    (p: any) => String(p.id) === String(props.meetingId)
                );
                
                if (proposal) {
                    const participants = proposal.inPersonMeetingParticipants || [];
                    setLocalParticipants(participants);
                }
            }
        } catch (error) {
            console.error('Failed to refresh participants:', error);
        }
    };

    const handleAddParticipant = async (values: {
        name: string;
        role: string;
    }) => {
        try {
            setIsSubmitting(true);
            const response = await axios.post(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants`,
                {
                    name: values.name,
                    role: values.role,
                }
            );

            if (response.status === 200 || response.status === 201) {
                createNotification('Participant added successfully', 'success');
                setIsAddParticipantPopupVisible(false);
                await refreshParticipants();
            } else {
                createNotification('Failed to add participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to add participant', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (
        participant: EngagementInPersonMeetingParticipant
    ) => {
        setParticipantToDelete(participant);
        setIsDeleteConfirmationVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!participantToDelete) return;

        try {
            setIsDeleting(true);
            const response = await axios.delete(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants/${participantToDelete.id}`
            );

            if (response.status === 200 || response.status === 204) {
                createNotification(
                    'Participant deleted successfully',
                    'success'
                );
                setIsDeleteConfirmationVisible(false);
                setParticipantToDelete(null);
                await refreshParticipants();
            } else {
                createNotification('Failed to delete participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to delete participant', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const getPartyDisplayName = (partyType: TimelineParty): string => {
        switch (partyType) {
            case TimelineParty.Buyer:
                return 'Buyer';
            case TimelineParty.Seller:
                return 'Seller';
            default:
                return 'Unknown';
        }
    };

    const getColumns = () => {
        const columns: any[] = [
            {
                dataField: 'name',
                text: 'Name',
                headerClasses: 'name-column-header',
                sort: true,
            },
            {
                dataField: 'party',
                text: 'Representing',
                headerClasses: 'representing-column-header',
                sort: true,
                formatter: (cell: TimelineParty) => getPartyDisplayName(cell),
            },
            {
                dataField: 'role',
                text: 'Title/Role',
                headerClasses: 'role-column-header',
                sort: true,
            },
        ];

        if ((party === TimelineParty.Buyer || party === TimelineParty.Seller) && !props.isReadOnly) {
            columns.push({
                dataField: 'actions',
                text: 'Actions',
                sort: false,
                formatter: (
                    _: any,
                    row: EngagementInPersonMeetingParticipant
                ) => {
                    if (!canDeleteParticipant(row)) {
                        return null;
                    }
                    return (
                        <div className="action-icons">
                            <FontAwesomeIcon
                                className="delete-button"
                                icon={faTrashCan}
                                onClick={() => handleDeleteClick(row)}
                                title="Delete participant"
                            />
                        </div>
                    );
                },
            });
        }

        return columns;
    };

    return (
        <div className="in-person-meeting-participants-modal">
            <Rodal
                width={800}
                height={600}
                visible={
                    props.isVisible &&
                    !isAddParticipantPopupVisible &&
                    !isDeleteConfirmationVisible
                }
                animation={'slideDown'}
                onClose={props.onClose}
                center>
                <ModalHeader>In-person meeting participants</ModalHeader>
                <ModalBody className="in-person-meeting-participants-modal-body">
                    <BootstrapTable
                        bootstrap4
                        striped
                        bordered
                        keyField="id"
                        data={sortedParticipants}
                        columns={getColumns()}
                        noDataIndication="No participants found"
                        rowStyle={{ height: '44px' }}
                    />
                    {!props.isReadOnly && (
                        <div className="add-participant-section">
                            <Button
                                color="primary"
                                onClick={() =>
                                    setIsAddParticipantPopupVisible(true)
                                }>
                                Add New Participant
                            </Button>
                        </div>
                    )}
                </ModalBody>
            </Rodal>

            <AddParticipantFormPopup
                isVisible={isAddParticipantPopupVisible}
                onClose={() => setIsAddParticipantPopupVisible(false)}
                onSubmit={handleAddParticipant}
                isSubmitting={isSubmitting}
            />

            <ConfirmationModal
                isVisible={isDeleteConfirmationVisible}
                setIsVisible={setIsDeleteConfirmationVisible}
                header="Confirmation"
                confirmationText={`Are you sure you want to remove '${participantToDelete?.name}'?`}
                onConfirm={handleDeleteConfirm}
                nextButtonText="Confirm"
                cancelButtonText="Back"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default InPersonMeetingParticipantsModal;
